import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { routeInfo, userCredentials } from 'recoilState/GlobalState';

import s from "styles/pages/Pages.module.scss"
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal, ViewModal } from "./Actions/index";
import { Buttons, HeaderButton } from "components";
import Filters from "components/Filters";
import { Autocomplete, TextField } from '@mui/material';
import { ACTIONS } from "consts/Actions";
import { Add, Cached, EditOutlined, DeleteOutlineRounded, VisibilityRounded } from '@mui/icons-material';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import { useFormik } from 'formik';
import { useService, puedeEditarServicio } from 'components/route/ServiceRoute';
import { estructuraConPermisosTitles as titles } from "consts/titulos-de-tablas";
import { etiquetaSiNo } from "consts/banderas";
import XGrid from 'components/UI/Grid/XGrid';
import { RNHEstablecimientoGet } from 'services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet';

// Opción del buscador para ver los CAPS que todavía no cuelgan de ningún hospital.
// Es el caso que uno busca cuando entra a asignar.
const SIN_ASIGNAR = { id: "__SIN_ASIGNAR__", label: "Sin asignar" };

export default function CAPS() {
  const setRoute = useSetRecoilState(routeInfo);
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} })
  const [caps, setCaps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // DESESTRUCTURADO: `useService()` devuelve el contexto del Outlet, que es
  // `{ service }`. Sin las llaves, `service` era ese envoltorio y cualquier campo
  // que se le pidiera daba undefined —incluido `servicioPermiso`, que dejaba los
  // botones de ABM apagados para todos los roles—.
  const { service } = useService()
  const credentials = useRecoilValue(userCredentials);

  // Catálogo del buscador: todos los hospitales, más la opción "Sin asignar".
  const [opcionesHospital, setOpcionesHospital] = useState([SIN_ASIGNAR]);
  const [cargandoHospitales, setCargandoHospitales] = useState(false);

  // Filtro APLICADO (el que viaja al back), separado del que se está eligiendo en el
  // Autocomplete: el filtro recién se aplica al tocar "Filtrar".
  const [filtroAplicado, setFiltroAplicado] = useState(null);

  const formik = useFormik({
    initialValues: { hospital: null },
    // El valor va por parametro ademas de al state: `setFiltroAplicado` no es inmediato y la
    // carga necesita el hospital recien elegido, no el del render anterior.
    onSubmit: (values) => {
      setFiltroAplicado(values.hospital);
      getCapsData(values.hospital);
    },
  });
  const { hospital } = formik.values;

  /**
   * Trae TODOS los CAPS del filtro y los deja en el state. La paginacion la hace la grilla.
   *
   * @param filtro el hospital elegido. Por defecto el que ya estaba aplicado, que es lo que
   *               corresponde cuando quien llama es "Actualizar" o un modal que acaba de
   *               guardar. Solo el submit del buscador pasa uno nuevo.
   */
  const getCapsData = async (filtro = filtroAplicado) => {
    setRowSelected({ status: false, row: {} });
    setCaps([]);
    setIsLoading(true);

    const sinAsignar = filtro?.id === SIN_ASIGNAR.id;

    // `establecimientoCod` es el establecimiento de CONTEXTO, no el del registro que se
    // busca: con `tipo: 'C'` el back devuelve los CAPS que cuelgan de ese hospital.
    //
    // `soloDisponibles` funciona como MODO, no como booleano:
    //   'A' → los asignados al hospital de `establecimientoCod`
    //   'Y' → los que no cuelgan de ningun hospital ("Sin asignar")
    //   ''  → sin filtro, todos los CAPS
    const codHospital = sinAsignar ? '' : (filtro?.cod ?? '');
    // Sin `page` ni `pageSize`: se pide el listado completo y la grilla lo pagina sola, igual
    // que en el resto de las vistas de estructuras.
    const { data: result } = await RNHEstablecimientoGet(credentials, {
      tipo: 'C',
      hospitalCod: codHospital,
      disponibles: sinAsignar ? 'Y' : (codHospital ? 'A' : ''),
    })

    const filas = result[0]?.rnhEstablecimientoId ? result.map((item) => {
      return {
        id: item.rnhEstablecimientoId,
        tipo: item.rnhEstablecimientoTipo,
        name: item.rnhEstablecimientoNombre,
        cod: item.rnhEstablecimientoCod,
        localidad: item.localidadNombre,
        domicilio: `${item.rnhEstablecimientoCalle} ${item.rnhEstablecimientoNumero}, ${item.localidadNombre}`,
        hospitalAsignado: item.rnhEstablecimientoAsignadoNombre,
        // No son columnas: viajan en la fila para que Modificar, Eliminar y Consultar no
        // tengan que volver a pedir el registro. La response ya los trae todos.
        version: item.rnhEstablecimientoVersion,
        calle: item.rnhEstablecimientoCalle ?? "",
        numero: item.rnhEstablecimientoNumero ?? "",
        cpa: item.rnhEstablecimientoCPA ?? "",
        localidadId: item.rnhEstablecimientoLocalidadId ?? "",
        lat: item.rnhEstablecimientoLatitud,
        lng: item.rnhEstablecimientoLongitud,

        // Los dos permisos, en sus dos formas: la ETIQUETA para la celda y el CRUDO
        // "Y"/"N" que precarga el modal de edicion.
        rotativos: etiquetaSiNo(item.rnhEstablecimientoAdmiteRotativos),
        guardias: etiquetaSiNo(item.rnhEstablecimientoAdmiteGuardias),
        admiteRotativos: item.rnhEstablecimientoAdmiteRotativos,
        admiteGuardias: item.rnhEstablecimientoAdmiteGuardias,
      };
    }) : [];

    setCaps(filas);
    setIsLoading(false);
  };

  // Catálogo de hospitales para el buscador. Va una sola vez: es el listado completo,
  // no depende del filtro aplicado.
  const getOpcionesHospital = useCallback(async () => {
    setCargandoHospitales(true);
    const { data: result } = await RNHEstablecimientoGet(credentials, { tipo: 'H' });
    const hospitales = result?.[0]?.rnhEstablecimientoId
      ? result.map((h) => ({
        id: h.rnhEstablecimientoId,
        cod: h.rnhEstablecimientoCod ?? "",
        label: h.rnhEstablecimientoNombre ?? "",
      }))
      : [];
    setOpcionesHospital([SIN_ASIGNAR, ...hospitales]);
    setCargandoHospitales(false);
  }, [credentials]);

  const borrarFiltros = () => {
    formik.setFieldValue("hospital", null);
    setFiltroAplicado(null);
    getCapsData(null);
  };

  const handleClick = ({ procede, title }) => {
    setModalDetails({ procede, title, isOpen: true });
  };
  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };

  useEffect(() => {
    getOpcionesHospital()
    getCapsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.servicioNombre])

  useLayoutEffect(() => {
    setRoute({
      wordKey: `CAPS`
    })
  }, [service.servicioNombre, setRoute])

  return (
    <main className={s.main}>
      {/* El alta, la modificacion y la consulta abren con size="lg" (900px), la talla pensada para dos columnas con mapa. */}
      <Modal size={modalDetails.procede === ACTIONS.DELETE ? "sm" : modalDetails.procede === ACTIONS.INSERT || modalDetails.procede === ACTIONS.UPDATE || modalDetails.procede === ACTIONS.VIEW ? "lg" : undefined} big={modalDetails.procede === ACTIONS.HISTORY || modalDetails.procede === ACTIONS.VIEW} compact={modalDetails.procede === ACTIONS.DETAIL || modalDetails.procede === ACTIONS.VIEW || modalDetails.procede === ACTIONS.INSERT || modalDetails.procede === ACTIONS.UPDATE} open={modalDetails.isOpen} title={modalDetails.title} onClose={closeModal}>
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            row={rowSelected.row}
            updateList={getCapsData}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <DeleteModal
            row={rowSelected.row}
            servicio={service}
            updateList={getCapsData}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.VIEW && (
          <ViewModal
            row={rowSelected.row}
            servicio={service}
            updateList={getCapsData}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            servicio={service}
            updateList={getCapsData}
            closeModal={closeModal}
          />
        )}

      </Modal>
      <div className={s["filters-actions"]}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* El buscador va como children de Filters: el componente compartido solo
              soporta text/select/date, y children se renderiza antes del botón
              "Filtrar" sin tener que tocarlo. */}
          <Filters formik={formik} inputsRender={[]} disabled={!hospital}>
            <Autocomplete
              value={hospital}
              onChange={(_evento, nuevo) => formik.setFieldValue("hospital", nuevo)}
              options={opcionesHospital}
              loading={cargandoHospitales}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, valor) => option.id === valor.id}
              sx={{ width: 280, marginRight: "0.5rem" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Hospital"
                  variant="standard"
                  placeholder="Buscar hospital..."
                />
              )}
            />
          </Filters>
          <HeaderButton
            text={"Borrar filtros"}
            onClick={borrarFiltros}
            disabled={!hospital}
          >
            <FilterAltOffRoundedIcon />
          </HeaderButton>
        </div>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => getCapsData()}
            disabled={rowSelected.row.id || false}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Detalle del CAPS"}
            handleClick={handleClick}
            data={{
              title: `${rowSelected.row.name} - Detalle `,
              procede: ACTIONS.VIEW
            }}
            disabled={!rowSelected.row.id}
          >
            <VisibilityRounded />
          </HeaderButton>
          <HeaderButton
            text={"Nuevo CAPS"}
            handleClick={handleClick}
            data={{
              title: "Nuevo CAPS",
              procede: ACTIONS.INSERT
            }}
            disabled={!puedeEditarServicio(service) || (rowSelected.row.id)}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Modificar CAPS"}
            handleClick={handleClick}
            data={{
              title: "Modificar CAPS",
              procede: ACTIONS.UPDATE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <EditOutlined />
          </HeaderButton>
          <HeaderButton
            text={"Eliminar CAPS"}
            handleClick={handleClick}
            data={{
              title: "Eliminar CAPS",
              procede: ACTIONS.DELETE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <DeleteOutlineRounded />
          </HeaderButton>
        </Buttons>
      </div>

      <div className={s.container}>
        <XGrid
          rows={caps}
          titles={titles}
          loading={isLoading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  )
}
